// Axios
import axios from 'axios';

// Library
import _ from 'lodash';

// Constants
import STATUS_LIST from '@/constants/mail/status_list';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import DataUtil from '@/utils/DataUtil';
import DateUtil from '@/utils/DateUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import ValidationUtil from '@/utils/ValidationUtil';

export const actionTypes = {
    getList: 'getList',
    clearAllFilters: 'clearAllFilters',
    reset: 'reset',
    clearAdvancedFilters: 'clearAdvancedFilters',
    resetAdvancedFiltersAndResults: 'resetAdvancedFiltersAndResults',
    setResultAsVoid: 'setResultAsVoid',
    setMailItemDetails: 'setMailItemDetails',
    clearCurrentMailItem: 'clearCurrentMailItem',
};
export const mutationTypes = {
    SET_RESULTS_LIST: 'SET_RESULTS_LIST',
    SET_RESULTS_IN_FLIGHT: 'SET_RESULTS_IN_FLIGHT',
    SET_RESULTS_HAS_SEARCHED: 'SET_RESULTS_HAS_SEARCHED',
    SET_RESULTS_CANCEL_TOKEN: 'SET_RESULTS_CANCEL_TOKEN',

    SET_FILTER_CUSTOMER: 'SET_FILTER_CUSTOMER',
    SET_FILTER_SENDER: 'SET_FILTER_SENDER',
    SET_FILTER_TRACKING_NUMBER: 'SET_FILTER_TRACKING_NUMBER',
    SET_FILTER_MAIL_TYPE: 'SET_FILTER_MAIL_TYPE',
    SET_FILTER_DATE_RANGE: 'SET_FILTER_DATE_RANGE',
    SET_FILTER_DATE_RANGE_START: 'SET_FILTER_DATE_RANGE_START',
    SET_FILTER_DATE_RANGE_END: 'SET_FILTER_DATE_RANGE_END',
    SET_FILTER_DATE_RANGE_MAX_START: 'SET_FILTER_DATE_RANGE_MAX_START',
    SET_FILTER_DATE_RANGE_MAX_END: 'SET_FILTER_DATE_RANGE_MAX_END',
    SET_FILTER_DATE_RANGE_MINIMUM: 'SET_FILTER_DATE_RANGE_MINIMUM',
    SET_FILTER_CUSTOMER_TYPE: 'SET_FILTER_CUSTOMER_TYPE',
    SET_FILTER_STATUS_TYPE: 'SET_FILTER_STATUS_TYPE',
    SET_FILTER_ADVANCED_SEARCH: 'SET_FILTER_ADVANCED_SEARCH',

    SET_CURRENT_MAIL_ITEM: 'SET_CURRENT_MAIL_ITEM',
};

export const getterTypes = {
    resultList: 'resultList',
    hasResults: 'hasResults',
    itemExistsInResultListById: 'itemExistsInResultListById',
    getItemFromResultListById: 'getItemFromResultListById',
    noStartDate: 'noStartDate',
    noEndDate: 'noEndDate',
    isFilterStatusAll: 'isFilterStatusAll',
};

export default {
    namespaced: true,
    state: {
        /** @type { Object } */
        filters: {
            customer: null,
            sender: '',
            trackingNumber: '',
            mailType: {
                id: 0,
                name: 'ALL',
                friendly: 'All',
            },
            dateRange: {
                start: null,
                end: null,
                maxStart: DateUtil.startOfToday(),
                maxEnd: DateUtil.startOfToday(),
                minimum: null,
            },
            defaultDaysBefore: 30,
            customerType: {
                id: 0,
                name: 'ALL',
                friendly: 'All',
            },
            statusType: STATUS_LIST.open,
        },
        /** @type { Object } */
        results: {
            /** @type { Object } */
            list: {},
            /** @type { Boolean } */
            inFlight: false,
            /** @type { Boolean } */
            hasSearched: false,
            /** @type { Object | Null } */
            cancelToken: null,
        },
        /** @type { Object } */
        currentMailItem: {},
    },
    getters: {
        /** @returns { Array } */
        [ getterTypes.resultList ]: ( state ) => Object.values( state.results.list ),
        /** @returns { Boolean } */
        [ getterTypes.hasResults ]: ( state, getters ) => ( getters.resultList.length > 0 ),
        /** @returns { Function } -> Boolean */
        [ getterTypes.itemExistsInResultListById ]: ( state, getters ) => ( id ) => ( getters.hasResults && state.results.list[ id ] ),
        /** @returns { Function } -> Object | Null */
        [ getterTypes.getItemFromResultListById ]: ( state, getters ) => ( id ) => (
            getters.itemExistsInResultListById( id )
                ? state.results.list[ id ]
                : null
        ),
        [ getterTypes.noStartDate ]: ( state ) => ( state.filters.dateRange.start === null ),
        [ getterTypes.noEndDate ]: ( state ) => ( state.filters.dateRange.end === null ),
        /** @returns { Boolean } */
        [ getterTypes.isFilterStatusAll ]: ( state ) => ( state.filters.statusType.friendly === 'All' ),
    },
    actions: {
        async [ actionTypes.getList ]( context ) {
            context.commit( mutationTypes.SET_RESULTS_IN_FLIGHT, { boolean: true } );
            try {
                if ( context.state.results.cancelToken ) {
                    context.state.results.cancelToken.cancel( 'New request' );
                }
                const { CancelToken } = axios;
                const source = CancelToken.source();
                context.commit( mutationTypes.SET_RESULTS_CANCEL_TOKEN, { tokenSource: source } );

                // Set results.hasSearched to true if this is the first time searching
                if ( !context.state.results.hasSearched ) {
                    context.commit( mutationTypes.SET_RESULTS_HAS_SEARCHED, { boolean: true } );
                }

                const { data } = await LogisticsApi.searchMailItems( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    customerId: context.state.filters.customer?.customerId || null,
                    sender: context.state.filters.sender || null,
                    trackingNumber: context.state.filters.trackingNumber || null,
                    mailTypeId: context.state.filters.mailType.id || null,
                    receivedDateStart: context.state.filters.dateRange.start || null,
                    receivedDateEnd: context.state.filters.dateRange.end || null,
                    locatorId: context.state.filters.locatorId || null,
                    customerFilterTypeId: context.state.filters.customerType.id || null,
                    statusFilterTypeId: context.state.filters.statusType.id || null,
                    cancelToken: source.token,
                } );
                context.commit( mutationTypes.SET_RESULTS_LIST, {
                    results: ValidationUtil.arrayHasItems( data.mailSearchListDTO )
                        ? DataUtil.normalize( data.mailSearchListDTO, 'id' )
                        : {},
                } );
                context.commit( mutationTypes.SET_RESULTS_IN_FLIGHT, { boolean: false } );
            } catch ( error ) {
                // If the error was caused by something other than the cancelToken
                if ( !axios.isCancel( error ) ) {
                    console.error( 'Error searching for mail items: ', error );
                    context.commit( mutationTypes.SET_RESULTS_IN_FLIGHT, { boolean: false } );
                    context.commit( mutationTypes.SET_RESULTS_LIST, { results: {} } );
                    throw error;
                }
            } finally {
                context.commit( mutationTypes.SET_RESULTS_CANCEL_TOKEN, { tokenSource: null } );
            }
        },
        async [ actionTypes.clearAdvancedFilters ]( context ) {
            context.commit( mutationTypes.SET_FILTER_TRACKING_NUMBER, { trackingNumber: '' } );
            context.commit( mutationTypes.SET_FILTER_MAIL_TYPE, {
                mailType: {
                    id: 0,
                    name: 'ALL',
                    friendly: 'All',
                },
            } );
            context.commit( mutationTypes.SET_FILTER_CUSTOMER_TYPE, {
                customerType: {
                    id: 0,
                    name: 'ALL',
                    friendly: 'All',
                },
            } );
            context.commit( mutationTypes.SET_FILTER_STATUS_TYPE, {
                statusType: STATUS_LIST.open,
            } );
            context.commit( mutationTypes.SET_FILTER_DATE_RANGE, {
                dateRange: {
                    start: null,
                    end: null,
                    maxStart: DateUtil.startOfToday(),
                    maxEnd: DateUtil.startOfToday(),
                    minimum: null,
                },
            } );
        },
        async [ actionTypes.reset ]( context ) {
            await context.dispatch( actionTypes.clearAllFilters );
            context.commit( mutationTypes.SET_RESULTS_HAS_SEARCHED, { boolean: false } );
            context.commit( mutationTypes.SET_RESULTS_LIST, { results: {} } );
        },
        async [ actionTypes.clearAllFilters ]( context ) {
            context.commit( mutationTypes.SET_FILTER_CUSTOMER, { customer: null } );
            context.commit( mutationTypes.SET_FILTER_SENDER, { sender: '' } );
            await context.dispatch( actionTypes.clearAdvancedFilters );
        },
        /**
         * This action is used to update search results after setting a mail item to void.
         * This prevents us to have to make a new API call to get the updated mail item.
         * @function
         * @param { Object } context
         * @param { Object } payload
         * @param { String } payload.resultId - the id of the result
         */
        async [ actionTypes.setResultAsVoid ]( context, { resultId } ) {
            // Gets the current results as normalized object ( that's why not using getter )
            let deepCloneResults = _.cloneDeep( context.state.results.list );

            if ( context.getters.isFilterStatusAll ) { // If the status filter is "ALL" we need to update the status to "Voided"
                deepCloneResults[ resultId ].status = 'Voided';
            } else { // Else we need to remove the mail item from the results altogether
                deepCloneResults = DataUtil.normalize(
                    // Reject is like a reverse filter and returns an array. So, we normalize the results.
                    _.reject( deepCloneResults, ( item ) => item.id === resultId ), 'id',
                );
            }

            context.commit( mutationTypes.SET_RESULTS_LIST, {
                results: deepCloneResults,
            } );
        },
        /**
         * This action is used to aggregate a mail item's details.
         * @function
         * @param { Object } context
         * @param { String } mailItemId - the id of the mail item
         */
        async [ actionTypes.setMailItemDetails ]( context, mailItemId ) {
            context.commit( mutationTypes.SET_RESULTS_IN_FLIGHT, { boolean: true } );
            try {
                // Get mail item transactions
                const { data } = await LogisticsApi.getMailItemDetails( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    mailItemId,
                } );
                // Set mail item transactions with data or empty array
                context.commit( mutationTypes.SET_CURRENT_MAIL_ITEM, data?.mailItemTrackingHistory );
            } catch ( error ) {
                context.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
            } finally {
                context.commit( mutationTypes.SET_RESULTS_IN_FLIGHT, { boolean: false } );
            }
        },
        [ actionTypes.clearCurrentMailItem ]( context ) {
            context.commit( mutationTypes.SET_CURRENT_MAIL_ITEM, {} );
        },
    },
    mutations: {
        [ mutationTypes.SET_CURRENT_MAIL_ITEM ]( state, mailItemDetails ) {
            state.currentMailItem = mailItemDetails;
        },
        [ mutationTypes.SET_RESULTS_IN_FLIGHT ]( state, { boolean } ) {
            state.results.inFlight = boolean;
        },
        [ mutationTypes.SET_RESULTS_LIST ]( state, { results } ) {
            state.results.list = results || {};
        },
        [ mutationTypes.SET_RESULTS_CANCEL_TOKEN ]( state, { tokenSource } ) {
            state.results.cancelToken = tokenSource;
        },
        [ mutationTypes.SET_RESULTS_HAS_SEARCHED ]( state, { boolean } ) {
            state.results.hasSearched = boolean;
        },
        [ mutationTypes.SET_FILTER_CUSTOMER ]( state, { customer } ) {
            state.filters.customer = customer || null;
        },
        [ mutationTypes.SET_FILTER_SENDER ]( state, { sender } ) {
            state.filters.sender = sender || '';
        },
        [ mutationTypes.SET_FILTER_TRACKING_NUMBER ]( state, { trackingNumber } ) {
            state.filters.trackingNumber = trackingNumber || '';
        },
        [ mutationTypes.SET_FILTER_MAIL_TYPE ]( state, { mailType } ) {
            state.filters.mailType = mailType || {
                id: 0,
                name: 'ALL',
                friendly: 'All',
            };
        },
        [ mutationTypes.SET_FILTER_DATE_RANGE ]( state, { dateRange } ) {
            state.filters.dateRange = dateRange;
        },
        [ mutationTypes.SET_FILTER_DATE_RANGE_START ]( state, { start } ) {
            state.filters.dateRange.start = start;
        },
        [ mutationTypes.SET_FILTER_DATE_RANGE_END ]( state, { end } ) {
            state.filters.dateRange.end = end;
        },
        [ mutationTypes.SET_FILTER_DATE_RANGE_MAX_START ]( state, { maxStart } ) {
            state.filters.dateRange.maxStart = maxStart;
        },
        [ mutationTypes.SET_FILTER_DATE_RANGE_MAX_END ]( state, { maxEnd } ) {
            state.filters.dateRange.maxEnd = maxEnd;
        },
        [ mutationTypes.SET_FILTER_DATE_RANGE_MINIMUM ]( state, { minimum } ) {
            state.filters.dateRange.minimum = minimum;
        },
        [ mutationTypes.SET_FILTER_CUSTOMER_TYPE ]( state, { customerType } ) {
            state.filters.customerType = customerType || {
                id: 0,
                name: 'ALL',
                friendly: 'All',
            };
        },
        [ mutationTypes.SET_FILTER_STATUS_TYPE ]( state, { statusType } ) {
            state.filters.statusType = statusType;
        },
    },
};
